'use client';

import type { ComponentProps, MouseEvent, ReactNode } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

type HomeAnchorLinkProps = Omit<ComponentProps<typeof Link>, 'href'> & {
  href: string;
  children: ReactNode;
};

export function HomeAnchorLink({ href, children, ...props }: HomeAnchorLinkProps) {
  const pathname = usePathname();
  const isHomepageAnchor = pathname === '/' && href.startsWith('/#');

  if (isHomepageAnchor) {
    const hash = href.slice(1);

    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
      props.onClick?.(event);
      if (event.defaultPrevented) {
        return;
      }

      event.preventDefault();

      const el = document.getElementById(hash.slice(1));
      if (!el) {
        window.location.hash = hash;
        return;
      }

      window.history.pushState(null, '', hash);
      el.scrollIntoView({ behavior: 'smooth' });
    };

    return (
      <a href={hash} className={props.className} onClick={handleClick}>
        {children}
      </a>
    );
  }

  return (
    <Link href={href} {...props}>
      {children}
    </Link>
  );
}
